<template>
  <div v-if="attachments.length > 0">
    <div
      v-for="(attachment, index) in attachments"
      :key="index"
      class="attachment-card mb-2"
      :class="{
        'attachment-card--failed': attachment.uploadStatus === 'failed',
        'attachment-card--success': attachment.uploadStatus === 'success',
      }"
    >
      <div class="d-flex align-items-start">
        <!-- Upload Status Icon -->
        <div v-if="attachment.uploadStatus" class="mr-2 mt-1">
          <b-spinner v-if="attachment.uploadStatus === 'uploading'" small variant="primary" />
          <font-awesome-icon
            v-else-if="attachment.uploadStatus === 'success'"
            :icon="['fal', 'check-circle']"
            class="text-success"
          />
          <font-awesome-icon
            v-else-if="attachment.uploadStatus === 'failed'"
            :icon="['fal', 'exclamation-circle']"
            class="text-danger"
          />
        </div>

        <!-- File Icon -->
        <div class="attachment-icon mr-3">
          <font-awesome-icon :icon="['fal', getDocumentTypeIcon(attachment.documentType)]" size="2x" />
        </div>

        <!-- File Info -->
        <div class="flex-grow-1">
          <div class="font-weight-bold">{{ attachment.file.name }}</div>
          <div class="small text-muted d-flex">
            <div>{{ formatFileSize(attachment.file.size) }}</div>
            <div class="mx-2">·</div>
            <div>{{ getDocumentTypeLabel(attachment.documentType) }}</div>
            <template v-if="attachment.documentDate">
              <div class="mx-2">·</div>
              <div>{{ attachment.documentDate | msm_date }}</div>
            </template>
          </div>
          <div v-if="attachment.shortDescription" class="small text-muted">{{ attachment.shortDescription }}</div>
          <div v-if="attachment.uploadError" class="small text-danger mt-1">{{ attachment.uploadError }}</div>
        </div>

        <!-- Actions -->
        <div v-if="editable || deletable" class="d-flex">
          <b-button v-if="editable" variant="link" class="text-primary p-2" @click="editAttachment(index)">
            <font-awesome-icon :icon="['fal', 'edit']" />
          </b-button>
          <b-button v-if="deletable" variant="link" class="text-danger p-2" @click="deleteAttachment(index)">
            <font-awesome-icon :icon="['fal', 'trash-alt']" />
          </b-button>
        </div>
      </div>
    </div>

    <!-- Metadata Dialog for editing -->
    <AttachmentMetadataDialog
      :show="showEditDialog"
      :attachments="editingAttachmentArray"
      @attachment-updated="handleAttachmentUpdated"
      @hidden="handleEditDialogHidden"
    />
  </div>
</template>

<script>
import AttachmentMetadataDialog from "./AttachmentMetadataDialog.vue";
import SurveyRevisionMixin from "@/mixins/SurveyRevisionMixin";

export default {
  name: "AttachmentList",

  mixins: [SurveyRevisionMixin],

  components: {
    AttachmentMetadataDialog,
  },

  props: {
    attachments: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: true,
    },
    deletable: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      showEditDialog: false,
      editingAttachment: null,
      editingAttachmentIndex: null,
    };
  },

  computed: {
    editingAttachmentArray() {
      return this.editingAttachment ? [this.editingAttachment] : [];
    },
  },

  methods: {
    editAttachment(index) {
      this.editingAttachment = this.attachments[index];
      this.editingAttachmentIndex = index;
      this.showEditDialog = true;
    },

    async deleteAttachment(index) {
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('Webportal.Actions.InquiryWizard.Attachment.DeleteConfirm'), {
        title: this.$t('Webportal.Actions.InquiryWizard.Attachment.DeleteTitle'),
        okTitle: this.$t('Webportal.Delete'),
        cancelTitle: this.$t('Webportal.Cancel'),
        okVariant: "danger",
        hideHeaderClose: false,
      });

      if (confirmed) {
        this.$emit("attachment-deleted", index);
      }
    },

    handleAttachmentUpdated(updatedAttachment) {
      if (this.editingAttachmentIndex === null) return;

      this.$emit("attachment-updated", {
        index: this.editingAttachmentIndex,
        attachment: updatedAttachment,
      });
    },

    handleEditDialogHidden() {
      this.showEditDialog = false;
      this.editingAttachment = null;
      this.editingAttachmentIndex = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.attachment-card {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: #f8f9fa;

  &--failed {
    background-color: #fff5f5;
    border-color: #f5c6cb;
  }

  &--success {
    border-color: #c3e6cb;
  }
}

.attachment-icon {
  min-width: 50px;
  text-align: center;
  color: var(--primary);
}
</style>
